/**
 * Our Ecosystem — dedicated institutional page for the BSGOTECH corporate architecture.
 *
 * Adds a public /our-ecosystem route that visually explains:
 *   BSGOTECH  →  NeuNexus  →  (RENALIX | NeuGreen Energy)
 *
 * Reuses the shared EcosystemArchitecture diagram and adds capability cards
 * per level. Fully DE / EN / PT.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronRight, ArrowUpRight, Cpu, Cloud, Boxes, Database, Sparkles, Layers,
  HeartPulse, Activity, Brain, LineChart, SunMedium, Battery, Zap, BarChart3,
  Home, Building2,
} from "lucide-react";
import { LangCtx, LanguageSwitcher } from "@/i18n/neunexus";
import EcosystemArchitecture from "@/pages/bsgotech/EcosystemArchitecture";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  glass:   "rgba(255,255,255,0.045)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  green:   "#22C55E",
  teal:    "#14B8A6",
  rose:    "#F43F5E",
  blue:    "#3B82F6",
  purple:  "#A855F7",
  amber:   "#F59E0B",
};

const DATA = {
  en: {
    docTitle: "Our Ecosystem · BSGOTECH",
    back: "BSGOTECH",
    heroEyebrow: "Our Ecosystem",
    heroTitle: "One technology company. One platform. Two operational divisions.",
    heroSub: "BSGOTECH develops the technology infrastructure. NeuNexus integrates it into industrial and health ecosystems. RENALIX transforms healthcare. NeuGreen Energy powers sustainable infrastructure.",
    parent: {
      title: "BSGOTECH — Technology Company",
      lead: "The parent technology company responsible for engineering the infrastructure that powers every solution across the ecosystem.",
      items: [
        { icon: Cpu,      label: "Artificial Intelligence" },
        { icon: Layers,   label: "Software Engineering" },
        { icon: Cloud,    label: "Cloud Infrastructure" },
        { icon: Boxes,    label: "Digital Platforms" },
        { icon: Database, label: "Data Intelligence" },
        { icon: Sparkles, label: "Strategic Innovation" },
        { icon: Building2,label: "International Collaboration" },
      ],
    },
    platform: {
      title: "NeuNexus — Industrial Innovation Platform",
      lead: "The integration platform that connects technologies, partners and digital infrastructure into complete, real-world solutions.",
      motto: "We don't build every technology. We build the ecosystem that connects them.",
      note: "NeuNexus is not a robotics manufacturer or an energy company. It is the integration layer between technology and application.",
      cta: "Enter the platform",
    },
    health: {
      title: "RENALIX — AI Healthcare",
      lead: "Predictive renal healthcare grounded in real clinical data — from Pflege facilities to hospital wards.",
      items: [
        { icon: Brain,       label: "Artificial Intelligence" },
        { icon: HeartPulse,  label: "Kidney Healthcare" },
        { icon: Activity,    label: "Clinical Intelligence" },
        { icon: LineChart,   label: "Predictive Analytics" },
        { icon: Cloud,       label: "Digital Health" },
      ],
      cta: "Explore RENALIX",
    },
    energy: {
      title: "NeuGreen Energy — Renewable Energy",
      lead: "Independence through innovation — solar generation, storage and smart energy for residential and commercial use.",
      items: [
        { icon: SunMedium,   label: "Solar Energy" },
        { icon: Battery,     label: "Battery Storage" },
        { icon: Zap,         label: "Smart Energy" },
        { icon: BarChart3,   label: "Energy Monitoring" },
        { icon: Home,        label: "Commercial & Residential Solutions" },
      ],
      cta: "Explore NeuGreen",
    },
  },
  de: {
    docTitle: "Unser Ökosystem · BSGOTECH",
    back: "BSGOTECH",
    heroEyebrow: "Unser Ökosystem",
    heroTitle: "Ein Technologieunternehmen. Eine Plattform. Zwei operative Divisionen.",
    heroSub: "BSGOTECH entwickelt die Technologieinfrastruktur. NeuNexus integriert sie in industrielle und Gesundheits-Ökosysteme. RENALIX transformiert das Gesundheitswesen. NeuGreen Energy treibt nachhaltige Infrastruktur an.",
    parent: {
      title: "BSGOTECH — Technologieunternehmen",
      lead: "Die Muttergesellschaft entwickelt die Infrastruktur, die jede Lösung im Ökosystem antreibt.",
      items: [
        { icon: Cpu,      label: "Künstliche Intelligenz" },
        { icon: Layers,   label: "Software Engineering" },
        { icon: Cloud,    label: "Cloud-Infrastruktur" },
        { icon: Boxes,    label: "Digitale Plattformen" },
        { icon: Database, label: "Data Intelligence" },
        { icon: Sparkles, label: "Strategische Innovation" },
        { icon: Building2,label: "Internationale Zusammenarbeit" },
      ],
    },
    platform: {
      title: "NeuNexus — Industrielle Innovationsplattform",
      lead: "Die Integrationsplattform, die Technologien, Partner und digitale Infrastruktur zu vollständigen, praxisnahen Lösungen verbindet.",
      motto: "Wir bauen nicht jede Technologie. Wir bauen das Ökosystem, das sie verbindet.",
      note: "NeuNexus ist weder Roboterhersteller noch Energieunternehmen. Es ist die Integrationsschicht zwischen Technologie und Anwendung.",
      cta: "Zur Plattform",
    },
    health: {
      title: "RENALIX — KI-Gesundheit",
      lead: "Prädiktive Nephrologie auf Basis realer klinischer Daten — vom Pflegeheim bis zur Krankenhausstation.",
      items: [
        { icon: Brain,       label: "Künstliche Intelligenz" },
        { icon: HeartPulse,  label: "Nierengesundheit" },
        { icon: Activity,    label: "Klinische Intelligenz" },
        { icon: LineChart,   label: "Prädiktive Analytik" },
        { icon: Cloud,       label: "Digital Health" },
      ],
      cta: "RENALIX entdecken",
    },
    energy: {
      title: "NeuGreen Energy — Erneuerbare Energie",
      lead: "Unabhängigkeit durch Innovation — Solarerzeugung, Speicherung und Smart Energy für den privaten und gewerblichen Einsatz.",
      items: [
        { icon: SunMedium,   label: "Solarenergie" },
        { icon: Battery,     label: "Batteriespeicher" },
        { icon: Zap,         label: "Smart Energy" },
        { icon: BarChart3,   label: "Energiemonitoring" },
        { icon: Home,        label: "Gewerbe- und Privatlösungen" },
      ],
      cta: "NeuGreen entdecken",
    },
  },
  pt: {
    docTitle: "O nosso ecossistema · BSGOTECH",
    back: "BSGOTECH",
    heroEyebrow: "O nosso ecossistema",
    heroTitle: "Uma empresa tecnológica. Uma plataforma. Duas divisões operacionais.",
    heroSub: "A BSGOTECH desenvolve a infraestrutura tecnológica. A NeuNexus integra-a em ecossistemas industriais e de saúde. A RENALIX transforma a saúde. A NeuGreen Energy potencia infraestrutura sustentável.",
    parent: {
      title: "BSGOTECH — Empresa Tecnológica",
      lead: "A empresa-mãe responsável por engenhar a infraestrutura que sustenta cada solução do ecossistema.",
      items: [
        { icon: Cpu,      label: "Inteligência Artificial" },
        { icon: Layers,   label: "Engenharia de Software" },
        { icon: Cloud,    label: "Infraestrutura Cloud" },
        { icon: Boxes,    label: "Plataformas Digitais" },
        { icon: Database, label: "Data Intelligence" },
        { icon: Sparkles, label: "Inovação Estratégica" },
        { icon: Building2,label: "Colaboração Internacional" },
      ],
    },
    platform: {
      title: "NeuNexus — Plataforma de Inovação Industrial",
      lead: "A plataforma de integração que liga tecnologias, parceiros e infraestrutura digital em soluções completas e reais.",
      motto: "Não construímos toda a tecnologia. Construímos o ecossistema que as liga.",
      note: "A NeuNexus não é fabricante de robótica nem empresa de energia. É a camada de integração entre tecnologia e aplicação.",
      cta: "Entrar na plataforma",
    },
    health: {
      title: "RENALIX — Saúde com IA",
      lead: "Cuidado renal preditivo baseado em dados clínicos reais — desde unidades de Pflege até enfermarias hospitalares.",
      items: [
        { icon: Brain,       label: "Inteligência Artificial" },
        { icon: HeartPulse,  label: "Saúde Renal" },
        { icon: Activity,    label: "Inteligência Clínica" },
        { icon: LineChart,   label: "Analítica Preditiva" },
        { icon: Cloud,       label: "Saúde Digital" },
      ],
      cta: "Explorar RENALIX",
    },
    energy: {
      title: "NeuGreen Energy — Energia Renovável",
      lead: "Independência através de inovação — geração solar, armazenamento e energia inteligente para uso comercial e residencial.",
      items: [
        { icon: SunMedium,   label: "Energia Solar" },
        { icon: Battery,     label: "Armazenamento em Bateria" },
        { icon: Zap,         label: "Energia Inteligente" },
        { icon: BarChart3,   label: "Monitorização de Energia" },
        { icon: Home,        label: "Soluções Residenciais e Comerciais" },
      ],
      cta: "Explorar NeuGreen",
    },
  },
};

const useO = () => {
  const { lang } = React.useContext(LangCtx);
  return DATA[lang] || DATA.en;
};

// -------------------------------------------------------- Sub-components

const CapabilityCard = ({ items, color, testid }) => (
  <div className="grid sm:grid-cols-2 gap-2" data-testid={testid}>
    {items.map((it, i) => {
      const Ic = it.icon;
      return (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ delay: i * 0.06, duration: 0.45 }}
          className="rounded-md px-4 py-3 flex items-center gap-3"
          style={{ background: P.surface, border: `1px solid ${P.border}` }}
        >
          <Ic size={16} style={{ color }} strokeWidth={1.75} />
          <div className="text-[12.5px]" style={{ color: P.ink }}>{it.label}</div>
        </motion.div>
      );
    })}
  </div>
);

const LevelBlock = ({ tag, title, lead, color, children, cta, testid }) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55 }}
    className="rounded-xl overflow-hidden"
    style={{
      background: `linear-gradient(135deg, ${color}0d 0%, ${P.bg2} 55%)`,
      border: `1px solid ${color}44`,
    }}
    data-testid={testid}
  >
    <div className="p-8 lg:p-10">
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color }}>{tag}</div>
      <h3 className="mt-3 font-semibold tracking-tight text-[22px] lg:text-[28px] leading-[1.15]"
        style={{ color: "#fff", letterSpacing: "-0.01em" }}>{title}</h3>
      <p className="mt-3 max-w-2xl text-[13.5px] leading-relaxed" style={{ color: P.dim }}>{lead}</p>
      <div className="mt-6">{children}</div>
      {cta}
    </div>
  </motion.section>
);

// -------------------------------------------------------- Page

export default function OurEcosystem() {
  const t = useO();
  React.useEffect(() => { document.title = t.docTitle; }, [t.docTitle]);

  return (
    <div className="relative min-h-screen" style={{ background: P.bg, color: P.ink }} data-testid="page-our-ecosystem">
      <LanguageSwitcher />

      {/* Top bar */}
      <div className="sticky top-0 z-40" style={{ background: "rgba(8,11,18,0.85)", borderBottom: `1px solid ${P.border}`, backdropFilter: "blur(12px)" }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-[12px]" style={{ color: P.silver }} data-testid="ecosystem-back-home">
            <ChevronRight size={13} className="rotate-180" />
            <span className="uppercase tracking-[0.28em]">{t.back}</span>
          </Link>
          <div className="hidden md:block text-[11px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>
            {t.heroEyebrow}
          </div>
          <div />
        </div>
      </div>

      {/* Hero */}
      <header className="relative overflow-hidden border-b" style={{ borderColor: P.border }}>
        {/* strategic Nano Banana hero backdrop */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
          backgroundImage: "url('/bsgotech/strategic/industrial-intelligence.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.35,
        }} />
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
          background: `linear-gradient(180deg, rgba(8,11,18,0.5) 0%, rgba(8,11,18,0.85) 60%, ${P.bg} 100%)`,
        }} />
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
          backgroundImage: `radial-gradient(circle at 20% 30%, ${P.blue}22 0%, transparent 55%), radial-gradient(circle at 80% 70%, ${P.green}22 0%, transparent 55%)`,
        }} />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="text-[10px] uppercase tracking-[0.42em] font-semibold" style={{ color: P.green }}>{t.heroEyebrow}</div>
          <h1 className="mt-5 font-semibold tracking-tight text-[36px] sm:text-[48px] lg:text-[64px] leading-[1.03] max-w-4xl"
            style={{ color: "#fff", letterSpacing: "-0.02em" }}>
            {t.heroTitle}
          </h1>
          <p className="mt-6 max-w-3xl text-[15px] lg:text-[16.5px] leading-relaxed" style={{ color: P.dim }}>
            {t.heroSub}
          </p>
        </div>
      </header>

      {/* Animated diagram (shared component) */}
      <EcosystemArchitecture compact />

      {/* Level detail cards */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20 space-y-5">
        <LevelBlock
          testid="level-bsgotech"
          tag="BSGOTECH · Parent"
          title={t.parent.title}
          lead={t.parent.lead}
          color={P.blue}
        >
          <CapabilityCard items={t.parent.items} color={P.blue} testid="cap-bsgotech" />
        </LevelBlock>

        <LevelBlock
          testid="level-neunexus"
          tag="NeuNexus · Platform"
          title={t.platform.title}
          lead={t.platform.lead}
          color={P.green}
          cta={
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/neunexus" data-testid="level-neunexus-cta"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md"
                style={{ background: `${P.green}18`, color: P.green, border: `1px solid ${P.green}55` }}>
                {t.platform.cta} <ArrowUpRight size={13} />
              </Link>
            </div>
          }
        >
          <div className="rounded-md p-5 italic text-[13.5px] leading-relaxed max-w-2xl"
            style={{ background: `${P.green}0a`, border: `1px solid ${P.green}22`, color: P.ink }}>
            &ldquo;{t.platform.motto}&rdquo;
          </div>
          <div className="mt-3 text-[12.5px] leading-relaxed max-w-2xl" style={{ color: P.dim }}>
            {t.platform.note}
          </div>
        </LevelBlock>

        <div className="grid lg:grid-cols-2 gap-5">
          <LevelBlock
            testid="level-renalix"
            tag="RENALIX · Division"
            title={t.health.title}
            lead={t.health.lead}
            color={P.rose}
            cta={
              <div className="mt-6">
                <Link to="/renalix" data-testid="level-renalix-cta"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md"
                  style={{ background: `${P.rose}18`, color: P.rose, border: `1px solid ${P.rose}55` }}>
                  {t.health.cta} <ArrowUpRight size={13} />
                </Link>
              </div>
            }
          >
            <CapabilityCard items={t.health.items} color={P.rose} testid="cap-renalix" />
          </LevelBlock>

          <LevelBlock
            testid="level-neugreen"
            tag="NeuGreen Energy · Division"
            title={t.energy.title}
            lead={t.energy.lead}
            color={P.teal}
            cta={
              <div className="mt-6">
                <Link to="/neugreen" data-testid="level-neugreen-cta"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md"
                  style={{ background: `${P.teal}18`, color: P.teal, border: `1px solid ${P.teal}55` }}>
                  {t.energy.cta} <ArrowUpRight size={13} />
                </Link>
              </div>
            }
          >
            <CapabilityCard items={t.energy.items} color={P.teal} testid="cap-neugreen" />
          </LevelBlock>
        </div>
      </div>
    </div>
  );
}
